import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import permissionsService from "@/services/permissionsService";
import { getUnifiedNavigation } from "@/navigation/unifiedNavigation";
import {
  selectActiveApp,
  setActiveApp,
} from "@/app/globalSlices/appStripSlice";
import { useSidebar } from "@/components/ui/sidebar";
import { appStripItems } from "./appStripConfig";
import { AppStripItemConfig, AppStripId } from "./appStripTypes";
import { flattenNavigationItems, isAppStripItemVisible } from "./visibility";

/** Default landing route per app, used when the item has no defaultRoute. */
export const appRoutes: Record<string, string> = {
  home: "/hub",
  resources: "/hub/resources",
  stochastic: "/lead-generation/sales-and-customer-dashboard",
  dashboards: "/hub/data-connector",
  labs: "/games/door-to-done",
  events: "/event-registration/events",
  "learning-center": "/hub/learning-center/directory",
  "call-intelligence": "/calls",
  community: "/hub/community",
  coaching: "/coaching/dashboard",
  "partner-network-new": "/partner-network/dashboard",
  admin: "/admin/companies",
  sales: "/sales/pipeline",
  "product-storefront": "/product-storefront/products",
};

interface AppStripItems {
  activeApp: string | null;
  visibleItems: AppStripItemConfig[];
  /**
   * Switch to an app: marks it active and navigates to its landing route.
   * On a phone the module menu sheet opens for apps that have one and closes
   * for standalone apps; on desktop `onAppClick` lets the layout expand the
   * wide sidebar instead.
   */
  openApp: (
    item: AppStripItemConfig,
    onAppClick?: (appId: AppStripId) => void,
  ) => void;
}

/**
 * The app strip's data: which apps this member can see, which is active, and
 * how to switch. Shared by the desktop rail and the phone menu sheet so both
 * apply the same visibility rules.
 */
export function useAppStripItems(): AppStripItems {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeApp = useSelector(selectActiveApp);
  const { isMobile, setOpenMobile } = useSidebar();
  const { hasPermission, hasRole, hasCertainPathCompany } =
    permissionsService();

  const allNavItems = flattenNavigationItems(getUnifiedNavigation());
  const isCertainPath = hasCertainPathCompany();

  const visibleItems = appStripItems.filter((item) =>
    isAppStripItemVisible(
      item,
      allNavItems,
      hasPermission,
      hasRole,
      isCertainPath,
    ),
  );

  const openApp: AppStripItems["openApp"] = (item, onAppClick) => {
    if (item.isExternal) return;

    dispatch(setActiveApp(item.id as AppStripId));

    const route =
      item.standalone && item.defaultRoute
        ? item.defaultRoute
        : appRoutes[item.id];
    if (route) navigate(route);

    if (isMobile) {
      setOpenMobile(!item.standalone);
    } else {
      onAppClick?.(item.id as AppStripId);
    }
  };

  return { activeApp, visibleItems, openApp };
}
